// @ts-nocheck
import './myTests.scss';

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router';
import { faFiles } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DefaultButton from '@components/Button/button';
import DataDisplayCell from '@components/DataDisplayCell/dataDisplayCell';
import DefaultModal from '@components/DefaultModal/defaultModal';
import Stepper from '@components/Stepper/stepper';
import {
  CURES_ACT_LANGUAGE,
  CURES_ACT_MODAL_BUTTONS_INFO,
  ORDER_STATUS_STEPS,
  RECEIPT_MODAL_BUTTON_INFO,
} from '@constants/constants';
import { OrderStatus } from '@customTypes/order';
import { Requisition } from '@customTypes/requisition';
import { isOrderReported } from '@helpers/order';
import { getRequisitionStatusKey } from '@helpers/requisition';
import PageContainer from '@layouts/container/PageContainer';
import RequestGeneticCounseling from '@pages/results/components/RequestGeneticCounseling/RequestGeneticCounseling';
import type { PaymentDetails } from '@store/actions';
import { fetchPaymentDetails, fetchRequisitionDetailsById, makeCuresActRelease } from '@store/actions';
import { Context } from '@store/store';
import { downloadCollatedReport } from '@utils/collatedReport';
import { formatDate, recordGoogleAnalyticsEvent, recordGoogleAnalyticsSend } from '@utils/helpers';

import OrderReceipt from './orderReceipt';
import ReceiptTemplate from './receiptTemplate';

function MyTests() {
  const [state, dispatch] = useContext(Context);
  const [showCuresActModal, setShowCuresActModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const { reqId: currentReqId } = useParams<{ reqId: string }>();
  const [accumulatedOrdersStatus, setAccumulatedOrdersStatus] = useState<OrderStatus | null>(null);
  const [allReportsCompleted, setAllReportsCompleted] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<PaymentDetails | null>(null);

  const cardSubtext = useMemo(() => {
    switch (accumulatedOrdersStatus) {
      case OrderStatus.COMPLETED:
        return 'Click the download button to view your report';
      case OrderStatus.CLINICIAN_REVIEW:
        return (
          <>
            <p className='mb-0 d-inline-block'>
              Please see your email for a link to schedule with a counselor. If you would like immediate release of your
              results,{' '}
            </p>
            <Button
              variant='link'
              size='sm'
              className='genetic_counseling_open_modal_button d-inline-block'
              onClick={() => setShowCuresActModal(true)}
              aria-label='open modal'
            >
              click here
            </Button>
          </>
        );
      case OrderStatus.CANCELED:
      case OrderStatus.DENIED:
        return 'Please contact support for more information';
      default:
        return 'We will send you an update once your report is ready';
    }
  }, [accumulatedOrdersStatus]);

  const mainDisplayText = useMemo(() => {
    switch (accumulatedOrdersStatus) {
      case OrderStatus.COMPLETED:
        return 'Your report is available for download';
      case OrderStatus.CLINICIAN_REVIEW:
        return 'Your report is being reviewed by your clinician';
      case OrderStatus.CANCELED:
        return 'Your report has been canceled';
      case OrderStatus.DENIED:
        return 'Your report has been denied';
      default:
        return 'Your report will be ready soon';
    }
  }, [accumulatedOrdersStatus]);

  const getTestRequests = useCallback(() => {
    fetchRequisitionDetailsById(currentReqId).then(res => {
      dispatch({
        type: 'SET_CURRENT_TEST_REQUEST',
        payload: res,
      });
      setOrderStatuses(res);
    });
  }, [currentReqId, dispatch]);

  useEffect(() => {
    let isMounted = true;
    setPaymentInfo(null);

    const loadPaymentInfo = async () => {
      try {
        const paymentInfoResponse = await fetchPaymentDetails(currentReqId);
        if (isMounted) setPaymentInfo(paymentInfoResponse);
      } catch {
        if (isMounted) setPaymentInfo(null);
      }
    };
    loadPaymentInfo();

    return () => {
      isMounted = false;
    };
  }, [currentReqId]);

  useEffect(() => {
    getTestRequests();
  }, [getTestRequests]);

  useEffect(() => {
    if (state.currentTestRequest) {
      setOrderStatuses(state.currentTestRequest);
    }
  }, [state.currentTestRequest]);

  useEffect(() => {
    recordGoogleAnalyticsSend('pageview', '/reports', 'Landing on Reports Page');
  }, []);

  const handleDownload = () => {
    window.frames.order_receipt_iframe.focus();
    window.frames.order_receipt_iframe.print();
  };

  // for the current requisition, filters through all orders to see if all are completed and sets status
  const setOrderStatuses = (currentRequisition: Requisition) => {
    setAllReportsCompleted((currentRequisition.orders || []).every(isOrderReported));

    // the first orders status is the "accumulated" status of all orders
    setAccumulatedOrdersStatus(getRequisitionStatusKey(currentRequisition));
  };

  const onModalBtnClick = type => {
    if (type === 'PRINT_RECEIPT') {
      recordGoogleAnalyticsEvent('User Interaction', 'Button Click', 'Print Order Receipt Button');
      handleDownload();
      setShowReceiptModal(false);
    } else if (type === 'REQUEST_WITHOUT_COUNSELING') {
      recordGoogleAnalyticsEvent('User Interaction', 'Button Click', 'Request Early Release');
      makeCuresActRelease({ requisition_id: currentReqId }).then(() => {
        // make call to fetch requisition status
        fetchRequisitionDetailsById(currentReqId).then(res => {
          dispatch({
            type: 'SET_CURRENT_TEST_REQUEST',
            payload: res,
          });
          setOrderStatuses(res);
        });
      });
      setShowCuresActModal(false);
    } else if (type === 'WAIT_FOR_RESULTS') {
      recordGoogleAnalyticsEvent('User Interaction', 'Button Click', 'Wait for Results');
      setShowCuresActModal(false);
    }
  };

  const handleReportDownload = () => {
    if (!currentReqId) return;
    downloadCollatedReport(currentReqId).catch(err => {
      console.error(err);
    });
  };

  return (
    <PageContainer>
      {Object.keys(state.currentTestRequest || {}).length === 0 ? (
        <div className='page_loading_spinner_wrapper text-primary'>
          <Spinner animation='border' />
        </div>
      ) : (
        <section className='tests_body_wrapper'>
          <div className='blue_banner_top tests_page_cell'>
            <h1 className='tests_title mb-0' data-testid='testsTitle'>
              MyOme Personal Genome
            </h1>
            {paymentInfo ? (
              <DefaultButton
                buttonLabel='View Receipt'
                styleClass='secondaryButtonStyling'
                buttonStyling={{
                  border: '1px solid #003595',
                  background: 'transparent',
                  color: '#003595',
                  padding: '5px',
                  fontSize: '11px',
                  minWidth: '100px',
                  margin: window.innerWidth < 800 ? '0 0 8px 0' : 0,
                }}
                onClick={() => setShowReceiptModal(true)}
                dataTestId='viewReceiptBtn'
              />
            ) : null}
          </div>
          <div className='tests_info_cell'>
            <DataDisplayCell
              dataLabel='Requisition Number'
              dataValue={currentReqId}
              dataLabelStyles={{ fontWeight: '600' }}
            />
            <DataDisplayCell
              dataLabel='Order Date'
              dataValue={formatDate(state.currentTestRequest.submitted_at)}
              dataLabelStyles={{ fontWeight: '600' }}
            />
          </div>
          {ORDER_STATUS_STEPS.find(o => o.step === accumulatedOrdersStatus) ? (
            <>
              <div style={{ marginTop: '12px' }}>
                <Stepper
                  currentStep={accumulatedOrdersStatus}
                  primaryColor='#003595'
                  secondaryColor='#36CBD9'
                  disabledColor='#ACAEB2'
                  totalSteps={ORDER_STATUS_STEPS}
                  containerSize={350}
                  showMobileStepper={true}
                  lineStylingMargin='0 -8px 10px -8px'
                />
              </div>
              <div className='tests_page_divider' style={{ marginTop: '18px' }} />
              <RequestGeneticCounseling requisitionId={currentReqId} />
            </>
          ) : null}
          <div className='tests_page_divider' />
          <h3 className='tests_secondary_title my-3'>Reports</h3>
          <div className='rounded-3 border mx-3'>
            <div className='reports_card_header tests_page_cell'>
              <div className='d-flex align-items-center'>
                <FontAwesomeIcon icon={faFiles} className='duotoneFilesIcon' fontSize={20} />
                <div>
                  <h4 className='reports_main_title mb-0'>{mainDisplayText}</h4>
                  <p className='reports_subtitle mb-0'>{cardSubtext}</p>
                </div>
              </div>
              <DefaultButton
                buttonLabel='Download Reports'
                styleClass='primaryButtonStyling'
                buttonStyling={{
                  padding: '8px',
                  marginTop: window.innerWidth < 800 ? '8px' : 0,
                  display: allReportsCompleted ? 'block' : 'none',
                  fontSize: '12px',
                  border: 'none',
                }}
                onClick={() => {
                  handleReportDownload();
                }}
                dataTestId='downloadReportsBtn'
              />
            </div>
            <div className='border-top py-3 px-3'>
              <h4 className='fw-semibold fs-sm text-secondary mb-1'>Downloads Include:</h4>
              <ul className='mb-0 fs-sm lh-lg list-style-none text-secondary'>
                {state.currentTestRequest.orders.map(ord => {
                  return <li key={ord.order_id}>{ord.product_name}</li>;
                })}
                <li>
                  DNAvisit Clinical Summary Letter{' '}
                  {accumulatedOrdersStatus === OrderStatus.CLINICIAN_REVIEW
                    ? '(Will be released after your counseling appointment)'
                    : ''}
                </li>
              </ul>
            </div>
          </div>
        </section>
      )}
      {/* eslint-disable-next-line */}
      {/* TODO: these modals are suboptimal and lack aria attributes. In most cases they are superfluous to the Reactstrap Modal component that already exists */}
      <DefaultModal
        show={showCuresActModal}
        modalTitle={'Request Access to Report Without Clinician Review'}
        modalButtons={CURES_ACT_MODAL_BUTTONS_INFO}
        onModalBtnClick={onModalBtnClick}
        toggleModal={setShowCuresActModal}
      >
        <p className='fs-sm lh-lg mb-0 py-3 px-4'>
          <span className='d-block'>{CURES_ACT_LANGUAGE.PART_ONE}</span>
          <span className='d-block'>{CURES_ACT_LANGUAGE.PART_TWO}</span>
        </p>
      </DefaultModal>
      {paymentInfo ? (
        <>
          <DefaultModal
            show={showReceiptModal}
            modalTitle={null}
            onModalBtnClick={onModalBtnClick}
            modalButtons={RECEIPT_MODAL_BUTTON_INFO}
            toggleModal={setShowReceiptModal}
          >
            <ReceiptTemplate paymentInfo={paymentInfo} requisition={state.currentTestRequest} />
          </DefaultModal>
          <OrderReceipt
            frame={'order_receipt_iframe'}
            paymentInfo={paymentInfo}
            requisition={state.currentTestRequest}
          />
        </>
      ) : null}
    </PageContainer>
  );
}

export default MyTests;
